import { BLANK_VALUE } from "../measurement/formatters";
import { BlockifyRequest, BlockifyResponse, TYPE_BLOCKIFY } from "./workerApi";
import {
  BlockRange,
  COLOR_WHITE,
  getBlockRange,
  getBlockStyle,
  getProperties,
  isCountableBlock,
  NormalizedBlock,
  normalizeJigsawBlock,
  normalizeOldBlock,
} from "portal/utils/blocks";
import { Bounds, sanitizeBounds } from "portal/utils/geo";
import {
  CROP_CATEGORY_BASE_METRIC_KEYS,
  CROP_CATEGORY_THINNING_METRIC_KEYS,
  getAvaliableSpatialMetricValue,
  makeCropCategoryMetric,
  makeWeedCategoryMetric,
  resolveSpatialMetric,
  SPATIAL_TIME,
  SPATIAL_TOTAL_WEEDS,
  SPATIAL_WEEDING_EFFICIENCY,
  SpatialMetricSelection,
} from "portal/utils/spatialMetrics";
import { DateTime } from "luxon";
import { Feature, Polygon } from "geojson";
import { featureCollection, polygon } from "@turf/helpers";
import { filteredListener } from "portal/utils/workers";
import {
  HISTORY_STATUS_IDLE,
  HISTORY_STATUS_LOADING,
  HistoryStatus,
} from "./useMapHistory";
import { isBlockRenderable } from "portal/utils/blockGeometry";
import {
  isSpatialMetricBoolean,
  isSpatialMetricDiscrete,
  isSpatialMetricNumber,
  SpatialMetric,
} from "portal/utils/metrics";
import { JigsawBlocksWeed } from "../../../../../protos/golang/generated/ts2/portal/jigsaw";
import {
  JigsawRobotHistory,
  RobotBlockHistory,
} from "portal/views/robots/useRobotHistory";
import { Layer, Source } from "react-map-gl";
import { LOCALSTORAGE_MAP_BORDERS } from "portal/utils/localStorage";
import { mapWorker } from "portal/workers";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useMeasurement, useSelf } from "portal/state/store";
import { useMountEffect } from "portal/utils/hooks/useMountEffect";
import { useTranslation } from "react-i18next";
import React, { ReactNode, useEffect, useMemo, useState } from "react";

const SOURCE_ID = "blocks";
const BLOCK_OUTLINE_LAYER_ID = "block-outlines";

// Block swaths are only ~400 ft² (~6 m), so when many spatially-spread jobs are
// framed at once the map fits to a low zoom where each swath is sub-pixel and
// the `fill` layers (which have no stroke) rasterize nothing — the heatmap
// looks empty even though the data is present. We draw an additional 1px line
// outline per block so every block always occupies at least one pixel and the
// worked field's shape stays visible at any zoom. Overlapping blocks simply
// overdraw the same pixels. `tolerance={0}` on the source keeps Mapbox from
// simplifying the tiny polygons away when it tiles the GeoJSON.

export const useMapBlocks = (
  blockHistory: RobotBlockHistory | undefined,
  selection: SpatialMetricSelection,
  isRelative: boolean,
  allowBorders: boolean = false,
  blockLabels?: string[]
): {
  blockSource: ReactNode;
  blockLayers: ReactNode[];
  blockRange?: BlockRange;
  bounds?: Bounds;
  historyStatus: HistoryStatus;
  interactiveLayerIds: string[];
} => {
  const { measurementSystem } = useSelf();
  const [historyStatus, setHistoryStatus] =
    useState<HistoryStatus>(HISTORY_STATUS_IDLE);

  const { cycleSlots } = useMeasurement();

  const { t, i18n } = useTranslation();

  const [requestBorders] = useLocalStorage(LOCALSTORAGE_MAP_BORDERS, false);
  const showBorders = requestBorders && allowBorders;

  const baseSelectedMetric = useMemo<SpatialMetric>(
    () => resolveSpatialMetric(selection) ?? SPATIAL_WEEDING_EFFICIENCY,
    [selection]
  );

  // Per-category metrics come from resolveSpatialMetric without a popup: it
  // depends on the full category list, which only the block data knows. The
  // weed-type popup lists every weed category's share, so those rows are
  // labeled with resolved category names.
  const jigsawCategories = useMemo((): JigsawRobotHistory | undefined => {
    if (blockHistory?.type !== "jigsaw") {
      return;
    }
    return blockHistory;
  }, [blockHistory]);
  const weedPopupRefs = useMemo<JigsawBlocksWeed[]>(
    () =>
      baseSelectedMetric.categoryKind === "weed"
        ? jigsawCategories?.weeds ?? []
        : [],
    [baseSelectedMetric, jigsawCategories]
  );

  const selectedMetric = useMemo<SpatialMetric>(() => {
    const { categoryId, categoryKind } = baseSelectedMetric;
    if (categoryId === undefined) {
      return baseSelectedMetric;
    }
    if (categoryKind === "weed") {
      return {
        ...baseSelectedMetric,
        popup: [
          ...weedPopupRefs.map(({ id, localizedName }) =>
            makeWeedCategoryMetric(id, localizedName)
          ),
          SPATIAL_TOTAL_WEEDS,
        ],
      };
    }
    const hasThinning =
      jigsawCategories?.crops.find(({ id }) => id === categoryId)
        ?.hasThinning ?? false;
    const popupKeys = hasThinning
      ? [
          ...CROP_CATEGORY_BASE_METRIC_KEYS,
          ...CROP_CATEGORY_THINNING_METRIC_KEYS,
        ]
      : [...CROP_CATEGORY_BASE_METRIC_KEYS];
    return {
      ...baseSelectedMetric,
      popup: popupKeys.map((key) => makeCropCategoryMetric(categoryId, key)),
    };
  }, [baseSelectedMetric, jigsawCategories, weedPopupRefs]);

  // do math in a seperate thread
  const [blockFeatures, setBlockFeatures] = useState<Feature<Polygon>[]>([]);
  useMountEffect(() => {
    mapWorker.addEventListener(
      "message",
      filteredListener((type, data) => {
        if (type === TYPE_BLOCKIFY) {
          setHistoryStatus(HISTORY_STATUS_IDLE);
          const { blocks } = data as BlockifyResponse;
          setBlockFeatures(blocks);
        }
      })
    );
  });

  const {
    calculatedBlocks,
    calculatedLabels,
    styleMapping,
    bounds,
    blockRange,
  } = useMemo<{
    styleMapping: string[];
    bounds: Bounds | undefined;
    blockRange: BlockRange | undefined;
    calculatedBlocks: NormalizedBlock[];
    calculatedLabels: (string | undefined)[];
  }>(() => {
    let hasBounds = false;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    const styleMapping: string[] = [];
    const calculatedBlocks: NormalizedBlock[] = [];
    const calculatedLabels: (string | undefined)[] = [];
    let bounds: Bounds | undefined;

    const blocks: NormalizedBlock[] | undefined =
      blockHistory?.type === "jigsaw"
        ? blockHistory.jigsawBlocks?.map((block) => normalizeJigsawBlock(block))
        : blockHistory?.blocks?.map((block) => normalizeOldBlock(block));

    const blockRange = getBlockRange(blocks, selectedMetric);

    const white = COLOR_WHITE.toString();

    for (const [blockIndex, block] of (blocks ?? []).entries()) {
      // ignore suspicious, mislocated, or positionless blocks
      if (!isBlockRenderable(block)) {
        continue;
      }

      const startX = block.start.latitude;
      const startY = block.start.longitude;
      const endX = block.end.latitude;
      const endY = block.end.longitude;

      if (isCountableBlock(block)) {
        hasBounds = true;
        minX = Math.min(minX, startX, endX);
        maxX = Math.max(maxX, startX, endX);
        minY = Math.min(minY, startY, endY);
        maxY = Math.max(maxY, startY, endY);
      }

      if (
        // don't render colors if metric isn't heatmappable
        !selectedMetric.canMap ||
        // don't render stuff that doesn't count
        (!isCountableBlock(block) && !selectedMetric.renderWhenNotCountable)
      ) {
        styleMapping.push(white);
      } else if (isSpatialMetricDiscrete(selectedMetric)) {
        const value = getAvaliableSpatialMetricValue(selectedMetric, block);
        styleMapping.push(
          selectedMetric.getColor(
            value,
            { min: 0, low: 0, medium: 0, high: 0, max: 0 },
            false
          )
        );
      } else if (isSpatialMetricBoolean(selectedMetric)) {
        const value = getAvaliableSpatialMetricValue(selectedMetric, block);
        const color = selectedMetric.getColor(
          value,
          {
            min: 0,
            low: 0,
            medium: 0,
            high: 1,
            max: 1,
          },
          isRelative
        );
        styleMapping.push(color);
      } else if (isSpatialMetricNumber(selectedMetric) && blockRange) {
        const value = getAvaliableSpatialMetricValue(selectedMetric, block);
        const color = selectedMetric.getColor(value, blockRange, isRelative);
        styleMapping.push(color);
      } else {
        styleMapping.push(white);
      }
      calculatedBlocks.push(block);
      calculatedLabels.push(blockLabels?.[blockIndex]);
    }
    if (hasBounds && minX !== maxX && minY !== maxY) {
      bounds = sanitizeBounds({ minX, maxX, minY, maxY });
    }

    return {
      calculatedBlocks,
      calculatedLabels,
      styleMapping,
      bounds,
      blockRange,
    };
  }, [blockHistory, blockLabels, selectedMetric, isRelative]);

  // Side effects must stay out of the memo above: a render-phase setState
  // re-renders synchronously, and per-category metrics can churn identity
  // until a commit lets useSelector's caches settle — React then hits its
  // re-render limit.
  useEffect(() => {
    const message: BlockifyRequest = {
      blocks: calculatedBlocks.filter(
        (block) =>
          !(
            block.originalBlock.type === "old" &&
            block.originalBlock.block.block === undefined
          )
      ),
      index: 0,
      type: TYPE_BLOCKIFY,
      units: "millimeters",
    };
    setHistoryStatus(HISTORY_STATUS_LOADING);
    mapWorker.postMessage(message);
  }, [calculatedBlocks]);

  // convert features to layers
  const { blockSource, blockLayers, interactiveLayerIds } = useMemo<{
    blockSource: ReactNode | undefined;
    blockLayers: ReactNode[];
    interactiveLayerIds: string[];
  }>(() => {
    const newBlockFeatures: Feature[] = [];
    const blockColors = new Set<string>();
    for (const [index, feature] of blockFeatures.entries()) {
      const block = calculatedBlocks[index];
      const color = styleMapping[index];
      if (!block || !color) {
        continue;
      }
      const label = calculatedLabels[index];
      blockColors.add(color);
      newBlockFeatures.push(
        polygon(feature.geometry.coordinates, {
          ...feature.properties,
          ...getProperties(
            t,
            i18n,
            measurementSystem,
            block,
            selectedMetric,
            cycleSlots
          ),
          _id: block.originalBlock.block.db?.id,
          _time: (() => {
            const time = getAvaliableSpatialMetricValue(SPATIAL_TIME, block);
            if (!time) {
              return BLANK_VALUE;
            }
            return DateTime.fromJSDate(time).toLocaleString(
              DateTime.DATETIME_MED_WITH_WEEKDAY,
              { locale: i18n.language }
            );
          })(),
          _timestamp: (() => {
            const time = getAvaliableSpatialMetricValue(SPATIAL_TIME, block);
            return time ? time.getTime() : 0;
          })(),
          _latitude: block.start?.latitude.toFixed(7),
          _longitude: block.start?.longitude.toFixed(7),
          _color: color,
          ...(label ? { _jobName: label } : {}),
        })
      );
    }
    const blockSource = (
      <Source
        id={SOURCE_ID}
        type="geojson"
        data={featureCollection(newBlockFeatures)}
        tolerance={0}
      />
    );
    const blockLayers: ReactNode[] = [];
    if (showBorders) {
      blockLayers.push(
        <Layer
          id="lines"
          key="lines"
          source={SOURCE_ID}
          type="line"
          paint={{ "line-color": "#fff" }}
        />
      );
    }
    for (const color of blockColors) {
      blockLayers.push(
        <Layer
          key={color}
          beforeId={showBorders ? "lines" : undefined}
          source={SOURCE_ID}
          {...getBlockStyle(color)}
          filter={["==", color, ["get", "_color"]]}
        />
      );
    }
    // A single data-driven 1px outline per block, keyed on the precomputed
    // `_color`. Because a line has a fixed pixel width, each block occupies at
    // least one pixel even when its fill is sub-pixel, so the field's footprint
    // stays visible at every zoom. At high zoom it just traces the (already
    // filled) blocks.
    if (blockColors.size > 0) {
      blockLayers.push(
        <Layer
          id={BLOCK_OUTLINE_LAYER_ID}
          key={BLOCK_OUTLINE_LAYER_ID}
          source={SOURCE_ID}
          type="line"
          paint={{
            "line-color": ["get", "_color"],
            "line-width": 1,
          }}
        />
      );
    }
    return {
      blockSource,
      blockLayers,
      interactiveLayerIds: [
        ...blockColors,
        ...(blockColors.size > 0 ? [BLOCK_OUTLINE_LAYER_ID] : []),
      ],
    };
  }, [
    blockFeatures,
    calculatedBlocks,
    calculatedLabels,
    selectedMetric,
    styleMapping,
    showBorders,
    t,
    measurementSystem,
    i18n,
    cycleSlots,
  ]);

  return {
    blockSource,
    blockLayers,
    blockRange,
    bounds,
    historyStatus,
    interactiveLayerIds,
  };
};
